import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SwyperLogo from '../components/SwypeRLogo';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (newCode.every(digit => digit !== '') && index === 5) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeString = code.join('')) => {
    if (codeString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-code`, {
        email: email,
        code: codeString
      });

      if (response.data.success) {
        // Navigate to profile creation with verified email
        navigate('/create-profile', { 
          state: { 
            email: email,
            verified: true 
          } 
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/resend-code`, {
        email: email
      });
      setResendCooldown(60); // 60 second cooldown
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center pt-[4px] px-4 pb-[4px]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg pt-0 px-8 pb-8">
          <div className="flex flex-col items-center mb-6">
            <div className="mb-0 -mt-4 flex justify-center w-full overflow-visible">
              <SwyperLogo width={200} height={180} />
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2 mt-2">Check Your Email</h2>
            <p className="text-sm text-gray-600 text-center">
              We sent a 6-digit code to
              <br />
              <span className="font-semibold text-purple-600">{email}</span>
            </p>
          </div>

          <div className="space-y-6">
            {/* 6-Digit Code Input */}
            <div>
              <div className="flex justify-center gap-2 mb-4" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={`otp-input-${index}`}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    disabled={loading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <p className="text-xs text-gray-500 text-center">
                Code expires in 10 minutes
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button
              onClick={() => handleVerify()}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              disabled={loading || code.some(d => !d)}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Verifying...
                </div>
              ) : (
                'Verify Code'
              )}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Didn't receive the code?{' '}
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                  className={`font-semibold ${
                    resendCooldown > 0 || loading
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-purple-600 hover:text-purple-700'
                  }`}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </p>
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate('/signup')}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                ← Change email address
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyCode;
